import { Form, FormLabel } from "@/components/ui/form";
import { currencies } from "@/data/currencies";
import { instrument_types } from "@/data/instrument_types";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { parseBooleanToYesNo, parseDate } from "@/lib/utils";

import { Diff } from "@/features/FormCard/Diff";
import {
  dateSchema,
  parseIntValue,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";

import {
  AutocompleteField,
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

const table_name = "a7_opcvm_underlyings";

const FormSchema = z.object({
  type_instrument_du_sous_jacent: z
    .string()
    .min(1, "Le type d'instrument type est requis"),
  devise_du_sous_jacent: z.string().min(3, "La devise est requise"),
  code_isin_du_sous_jacent: z.string().nullable(),
  a7_opcvm_issuer_id: z.preprocess(
    (val) => parseIntValue(val as string),
    z.number().nullable().optional(),
  ),
  a7_opcvm_issuer_name: z.string().nullable(),
  date_de_maturite: z.preprocess(
    (val) => parseToDateValue(val as string),
    dateSchema,
  ),
  subordination: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  restructuration: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
});

type SubmittedData = {
  subordination?: boolean;
  restructuration?: boolean;
  [key: string]: any;
};

type AdjustedData = {
  subordination?: string;
  restructuration?: string;
  [key: string]: any;
};

function adjustData(submittedData: SubmittedData): AdjustedData {
  return {
    ...submittedData,
    date_de_maturite: parseDate(submittedData.date_de_maturite),
    subordination: parseBooleanToYesNo(submittedData.subordination),
    restructuration: parseBooleanToYesNo(submittedData.restructuration),
  };
}

export function A7OPCVMUnderlyingsEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    adjustData,
    client: "a7",
    disable_translations_request: true,
    fieldsNotApplyTranslation: ["type_instrument_du_sous_jacent"],
  });

  return (
    <div className="flex justify-center">
      <Card className="p-4 bg-white">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardHeader className="text-lg font-bold text-gray-700">
              {t(`${table_name}.title`, {
                ns: "tables",
                defaultValue: "Financial instrument",
              })}{" "}
              {id}
            </CardHeader>
            <Divider />
            <CardBody className="pt-4 flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <SelectField
                  name="type_instrument_du_sous_jacent"
                  label={t(
                    `${table_name}.columns.type_instrument_du_sous_jacent`,
                    { ns: "tables", defaultValue: "Instrument Type" },
                  )}
                  isRequired={true}
                  options={instrument_types}
                  control={form.control}
                />
                <AutocompleteField
                  name="devise_du_sous_jacent"
                  label={t(`${table_name}.columns.devise_du_sous_jacent`, {
                    ns: "tables",
                    defaultValue: "Devise",
                  })}
                  isRequired={true}
                  options={currencies}
                  control={form.control}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <InputField
                  name="code_isin_du_sous_jacent"
                  label={t(`${table_name}.columns.code_isin_du_sous_jacent`, {
                    ns: "tables",
                    defaultValue: "ISIN",
                  })}
                  control={form.control}
                />
                <DatePickerField
                  name="date_de_maturite"
                  label={t(`${table_name}.columns.date_de_maturite`, {
                    ns: "tables",
                    defaultValue: "Maturité",
                  })}
                  control={form.control}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <InputField
                  name="a7_opcvm_issuer_id"
                  label={t(`${table_name}.columns.a7_opcvm_issuer_id`, {
                    ns: "tables",
                    defaultValue: "Émetteur Id",
                  })}
                  control={form.control}
                />
                <InputField
                  name="a7_opcvm_issuer_name"
                  label={t(`${table_name}.columns.a7_opcvm_issuer_name`, {
                    ns: "tables",
                    defaultValue: "Émetteur",
                  })}
                  control={form.control}
                  variant="underlined"
                  fieldOptions={{ isDisabled: true }}
                />
              </div>
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${
                  ["BOND", "LOAN", "CONVERTIBLE BOND"].includes(
                    form.watch("type_instrument_du_sous_jacent"),
                  )
                    ? "opacity-100 max-h-screen"
                    : "opacity-0 max-h-0"
                }`}
              >
                <div className="flex flex-col gap-3">
                  <FormLabel className="text-gray-700">
                    {t("sections.debt")}
                  </FormLabel>
                  <div className="flex flex-col gap-2">
                    <CheckboxField
                      name="subordination"
                      label={t(`${table_name}.columns.subordination`, {
                        ns: "tables",
                        defaultValue: "Subordination",
                      })}
                      control={form.control}
                    />
                    <CheckboxField
                      name="restructuration"
                      label={t(`${table_name}.columns.restructuration`, {
                        ns: "tables",
                        defaultValue: "Restructuration",
                      })}
                      control={form.control}
                    />
                  </div>
                </div>
              </div>
            </CardBody>
            <CardFooter>
              <FormActionsUpdate
                onReset={handleReset}
                onDelete={openDeleteModal}
                elementId={id}
                client="a7"
                isDirty={form.formState.isDirty}
              />
            </CardFooter>
          </form>
        </Form>
      </Card>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        isLoading={isUpdating}
        onClose={cancelUpdate}
        onConfirm={confirmUpdate}
        title={t("confirm_update")}
        children={changedFields ? Diff(changedFields) : t("no_changes")}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onDelete={closeDeleteModal}
      />
    </div>
  );
}
